import { useEffect, useRef } from 'react';
import { SubscribeReturn, TimelineMarkersConsumer } from '../TimelineMarkersContext';
import { MarkerRendererType, MarkerType, TimelineMarkerType } from '../markerType';

export interface CustomMarkerProps {
  date: number;
  children: MarkerRendererType;
  updateMarker: (marker: MarkerType) => void;
  subscribeMarker: (m: MarkerType) => SubscribeReturn;
}

function CustomMarkerInner({ date, children, updateMarker, subscribeMarker }: CustomMarkerProps) {
  const getMarkerRef = useRef<(() => MarkerType) | null>(null);

  useEffect(() => {
    const { unsubscribe, getMarker } = subscribeMarker({
      type: TimelineMarkerType.Custom,
      renderer: children,
      date,
    });
    getMarkerRef.current = getMarker;
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [subscribeMarker, date, children]);

  useEffect(() => {
    if (getMarkerRef.current) {
      const marker = getMarkerRef.current();
      updateMarker({ ...marker, date });
    }
  }, [date, updateMarker]);

  return null;
}

export type CustomMarkerWrapperProps = Pick<CustomMarkerProps, 'date' | 'children'>;

function CustomMarkerWrapper(props: CustomMarkerWrapperProps) {
  return (
    <TimelineMarkersConsumer>
      {({ subscribeMarker, updateMarker }) => (
        <CustomMarkerInner
          subscribeMarker={subscribeMarker}
          updateMarker={updateMarker}
          {...props}
        />
      )}
    </TimelineMarkersConsumer>
  );
}

CustomMarkerWrapper.displayName = 'CustomMarker';

export default CustomMarkerWrapper;
